import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/shared/lib/query-keys.ts";
import {
  getBackerLeague,
  getBackerSpecialization,
  getCategoryFamilyMatrix,
  getCoInvestorComparables,
  getEcosystemSnapshot,
  getFounderPedigree,
  getIntelligenceKpis,
  getRunwayStressList,
  getScoreFundingScatter,
  getStageMix,
  getStealthWatchlist,
} from "../api/intelligence-api.ts";

const STALE_MS = 60 * 1000;

export function useIntelligenceKpis() {
  return useQuery({
    queryKey: queryKeys.intelligence.kpis(),
    queryFn: getIntelligenceKpis,
    staleTime: STALE_MS,
  });
}

export function useCategoryFamilyMatrix() {
  return useQuery({
    queryKey: queryKeys.intelligence.categoryMatrix(),
    queryFn: getCategoryFamilyMatrix,
    staleTime: STALE_MS,
  });
}

export function useScoreFundingScatter() {
  return useQuery({
    queryKey: queryKeys.intelligence.scatter(),
    queryFn: getScoreFundingScatter,
    staleTime: STALE_MS,
  });
}

export function useBackerLeague(limit = 20) {
  return useQuery({
    queryKey: queryKeys.intelligence.backerLeague(limit),
    queryFn: () => getBackerLeague(limit),
    staleTime: STALE_MS,
  });
}

export function useBackerSpecialization(limit = 30) {
  return useQuery({
    queryKey: queryKeys.intelligence.backerSpecialization(limit),
    queryFn: () => getBackerSpecialization(limit),
    staleTime: STALE_MS,
  });
}

export function useCoInvestorComparables(projectId: string | null, minShared = 3) {
  return useQuery({
    queryKey: queryKeys.intelligence.coInvestors(projectId ?? "", minShared),
    queryFn: () => getCoInvestorComparables(projectId as string, minShared),
    staleTime: STALE_MS,
    enabled: Boolean(projectId),
  });
}

export function useRunwayStressList(limit = 50) {
  return useQuery({
    queryKey: queryKeys.intelligence.runwayStress(limit),
    queryFn: () => getRunwayStressList(limit),
    staleTime: STALE_MS,
  });
}

export function useStealthWatchlist(limit = 50) {
  return useQuery({
    queryKey: queryKeys.intelligence.stealthWatchlist(limit),
    queryFn: () => getStealthWatchlist(limit),
    staleTime: STALE_MS,
  });
}

export function useFounderPedigree() {
  return useQuery({
    queryKey: queryKeys.intelligence.founderPedigree(),
    queryFn: getFounderPedigree,
    staleTime: STALE_MS,
  });
}

export function useEcosystemSnapshot(minSample = 5) {
  return useQuery({
    queryKey: queryKeys.intelligence.ecosystemSnapshot(minSample),
    queryFn: () => getEcosystemSnapshot(minSample),
    staleTime: STALE_MS,
  });
}

export function useStageMix() {
  return useQuery({
    queryKey: queryKeys.intelligence.stageMix(),
    queryFn: getStageMix,
    staleTime: STALE_MS,
  });
}
